import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CheckinTrendChart } from "../components/CheckinTrendChart";
import { Users2, Activity, UserPlus, CheckCircle2, Clock, Globe, Download } from "lucide-react";

export function AdminOverview() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/clients-overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  const exportReport = async () => {
    try {
      const r = await api.get("/admin/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `report_team_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Esportazione non riuscita"); }
  };

  if (!data) return <div className="p-8 text-slate-400">Caricamento…</div>;

  const clients = filter === "all" ? data.clients : data.clients.filter((c) => c.owner_id === filter);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-8" data-testid="overview-view">
      <CheckinTrendChart />
      {/* Analytics */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-amber-400" />
          <h2 className="font-display font-bold text-lg">Report settimanale collaboratori</h2>
          <button data-testid="export-overview-button" onClick={exportReport}
            className="ml-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm hover:bg-white/10 transition-colors">
            <Download className="h-4 w-4" /> Esporta report (Excel)
          </button>
        </div>
        {analytics.length === 0 ? (
          <p className="text-slate-400 text-sm">Nessun collaboratore ancora.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {analytics.map((a, i) => (
              <motion.div key={a.owner_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="glass rounded-2xl p-5" data-testid={`analytics-${a.owner_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-display font-bold truncate">{a.name}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300">{a.role}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Stat icon={Users2} value={a.active_clients} label="Clienti" color="#10B981" />
                  <Stat icon={CheckCircle2} value={a.checkins_completed} label="Check-in ok" color="#22c55e" />
                  <Stat icon={Clock} value={a.checkins_pending} label="In sospeso" color="#f43f5e" />
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                  <UserPlus className="h-3.5 w-3.5 text-amber-400" />
                  <strong className="text-slate-200">{a.new_contacts_7d}</strong> nuovi contatti · ultimi 7 giorni
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Global clients table */}
      <section>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Globe className="h-5 w-5 text-slate-300" />
          <h2 className="font-display font-bold text-lg">Tutti i clienti</h2>
          <select data-testid="overview-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="ml-auto bg-[#14161d] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400/50">
            <option value="all">Tutti i collaboratori</option>
            {data.collaborators.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {clients.length === 0 ? (
          <p className="text-slate-400 text-sm">Nessun cliente trovato.</p>
        ) : (
          <div className="glass rounded-2xl overflow-hidden">
            <div className="hidden md:grid grid-cols-5 gap-3 px-4 py-3 text-xs text-slate-400 border-b border-white/10 font-semibold">
              <span>Cliente</span><span>Collaboratore</span><span>Stato</span><span>Ultimo check-in</span><span>Prossimo check-in</span>
            </div>
            {clients.map((c) => (
              <div key={c.id} data-testid={`overview-client-${c.id}`}
                className="grid grid-cols-2 md:grid-cols-5 gap-3 px-4 py-3 border-b border-white/5 text-sm items-center">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{c.name}</p>
                  {c.program && <p className="text-[11px] text-slate-500 truncate">{c.program}</p>}
                  {c.checkin_history && c.checkin_history.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.checkin_history.map((h) => (
                        <span key={h.day} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">G{h.day}: {h.date}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-slate-300 truncate">{c.owner_name}</span>
                <span className="text-emerald-300">{c.status}</span>
                <span className="text-slate-400">{c.last_checkin || "—"}</span>
                <span className="text-slate-400">{c.next_due || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 py-2.5">
      <Icon className="h-4 w-4 mx-auto mb-1" style={{ color }} />
      <p className="font-display font-bold text-lg leading-none">{value}</p>
      <p className="text-[10px] text-slate-400 mt-1">{label}</p>
    </div>
  );
}
